import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { userAPI } from '../services/api';
import './Account.css';

export default function Account() {
  const { user, logout, deleteAccount, updateUser, trialDaysRemaining } = useAuth();
  const navigate = useNavigate();

  // ── Name edit ────────────────────────────────────────────────────────────────
  const [nameEdit, setNameEdit]     = useState(false);
  const [nameVal, setNameVal]       = useState(user?.name || '');
  const [nameSaving, setNameSaving] = useState(false);

  const handleSaveName = async (e) => {
    e.preventDefault();
    if (!nameVal.trim() || nameVal.trim() === user?.name) { setNameEdit(false); return; }
    setNameSaving(true);
    try {
      const res = await userAPI.updateProfile(nameVal.trim());
      updateUser(res.data.user);
      setNameEdit(false);
      toast.success('Name updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update name');
    } finally {
      setNameSaving(false);
    }
  };

  // ── Password change ──────────────────────────────────────────────────────────
  const [pwForm, setPwForm]       = useState({ current: '', next: '', confirm: '' });
  const [pwSaving, setPwSaving]   = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.next !== pwForm.confirm) { toast.error('Passwords do not match'); return; }
    if (pwForm.next.length < 8)         { toast.error('Password must be at least 8 characters'); return; }
    setPwSaving(true);
    try {
      await userAPI.changePassword(pwForm.current, pwForm.next);
      setPwForm({ current: '', next: '', confirm: '' });
      toast.success('Password updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update password');
    } finally {
      setPwSaving(false);
    }
  };

  // ── Delete account ───────────────────────────────────────────────────────────
  const [showDelete, setShowDelete]       = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleDelete = async () => {
    if (deleteConfirm !== user?.name) return;
    setDeleteLoading(true);
    try {
      await deleteAccount();
      navigate('/');
    } catch {
      toast.error('Failed to delete account');
      setDeleteLoading(false);
    }
  };

  const planLabel = user?.plan === 'pro' ? '⚡ Pro'
                  : user?.plan === 'basic' ? 'Basic'
                  : 'Free Trial';

  const planExpiry = user?.plan_expires_at
    ? new Date(user.plan_expires_at).toLocaleDateString([], { dateStyle: 'medium' })
    : null;

  return (
    <div className="account-page">
      <div className="account-container">

        {/* ── Header ── */}
        <div className="account-nav">
          <button className="account-back" onClick={() => navigate('/dashboard')}>← Dashboard</button>
        </div>

        <div className="account-hero">
          <div className="account-avatar">{user?.name?.[0]?.toUpperCase() || '?'}</div>
          <div>
            <h1 className="account-name">{user?.name}</h1>
            <p className="account-email">{user?.email}</p>
          </div>
        </div>

        {/* ── Plan card ── */}
        <section className="account-section">
          <h2 className="account-section-title">Plan</h2>
          <div className="account-plan-row">
            <span className={`account-plan-badge account-plan-badge--${user?.plan}`}>{planLabel}</span>
            {user?.plan === 'trial' && trialDaysRemaining !== null && (
              <span className="account-plan-note">{trialDaysRemaining} days remaining</span>
            )}
            {planExpiry && user?.plan !== 'trial' && (
              <span className="account-plan-note">Renews {planExpiry}</span>
            )}
            <button className="account-btn-outline" onClick={() => navigate('/pricing')}>
              {user?.plan === 'trial' ? 'Upgrade Plan' : 'Manage Plan'}
            </button>
          </div>
        </section>

        {/* ── Profile ── */}
        <section className="account-section">
          <h2 className="account-section-title">Profile</h2>

          {/* Name */}
          <div className="account-field">
            <label className="account-label">Display name</label>
            {nameEdit ? (
              <form className="account-inline-form" onSubmit={handleSaveName}>
                <input
                  className="account-input"
                  value={nameVal}
                  onChange={e => setNameVal(e.target.value)}
                  maxLength={60}
                  autoFocus
                  required
                />
                <button className="account-btn-primary" type="submit" disabled={nameSaving}>
                  {nameSaving ? 'Saving…' : 'Save'}
                </button>
                <button className="account-btn-ghost" type="button" onClick={() => { setNameEdit(false); setNameVal(user?.name || ''); }}>
                  Cancel
                </button>
              </form>
            ) : (
              <div className="account-field-row">
                <span className="account-field-val">{user?.name}</span>
                <button className="account-btn-ghost" onClick={() => { setNameEdit(true); setNameVal(user?.name || ''); }}>
                  Edit
                </button>
              </div>
            )}
          </div>

          {/* Email — read-only */}
          <div className="account-field">
            <label className="account-label">Email</label>
            <div className="account-field-row">
              <span className="account-field-val">{user?.email}</span>
              {user?.is_google_user && <span className="account-google-tag">Google account</span>}
            </div>
          </div>
        </section>

        {/* ── Password — only for non-Google accounts ── */}
        {!user?.is_google_user && (
          <section className="account-section">
            <h2 className="account-section-title">Change Password</h2>
            <form className="account-pw-form" onSubmit={handleChangePassword}>
              <div className="account-field">
                <label className="account-label">Current password</label>
                <input
                  className="account-input"
                  type="password"
                  value={pwForm.current}
                  onChange={e => setPwForm(p => ({ ...p, current: e.target.value }))}
                  required
                  autoComplete="current-password"
                />
              </div>
              <div className="account-field">
                <label className="account-label">New password</label>
                <input
                  className="account-input"
                  type="password"
                  value={pwForm.next}
                  onChange={e => setPwForm(p => ({ ...p, next: e.target.value }))}
                  minLength={8}
                  required
                  autoComplete="new-password"
                />
              </div>
              <div className="account-field">
                <label className="account-label">Confirm new password</label>
                <input
                  className="account-input"
                  type="password"
                  value={pwForm.confirm}
                  onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))}
                  required
                  autoComplete="new-password"
                />
              </div>
              <button className="account-btn-primary" type="submit" disabled={pwSaving}>
                {pwSaving ? 'Updating…' : 'Update Password'}
              </button>
            </form>
          </section>
        )}

        {/* ── Danger zone ── */}
        <section className="account-section account-section--danger">
          <h2 className="account-section-title account-section-title--danger">Danger Zone</h2>
          {!showDelete ? (
            <div className="account-danger-row">
              <div>
                <p className="account-danger-label">Delete account</p>
                <p className="account-danger-sub">Permanently deletes your account, all meetings, recordings and transcripts. Cannot be undone.</p>
              </div>
              <button className="account-btn-danger" onClick={() => setShowDelete(true)}>
                Delete Account
              </button>
            </div>
          ) : (
            <motion.div
              className="account-delete-confirm"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <p className="account-danger-sub">Type your name <strong>{user?.name}</strong> to confirm permanent deletion:</p>
              <input
                className="account-input account-input--danger"
                type="text"
                placeholder={user?.name}
                value={deleteConfirm}
                onChange={e => setDeleteConfirm(e.target.value)}
                autoComplete="off"
              />
              <div className="account-delete-actions">
                <button
                  className="account-btn-danger"
                  onClick={handleDelete}
                  disabled={deleteConfirm !== user?.name || deleteLoading}
                >
                  {deleteLoading ? 'Deleting…' : 'Delete My Account'}
                </button>
                <button className="account-btn-ghost" onClick={() => { setShowDelete(false); setDeleteConfirm(''); }}>
                  Cancel
                </button>
              </div>
            </motion.div>
          )}
        </section>

      </div>
    </div>
  );
}
